import * as React from "react";
import HeaderMenu from "./HeaderMenu";
import { EngagementConfiguration, EndpointContextProps } from "../types";
import ConfigContext from "../context/ConfigContext";
import EndpointContext from "../context/EndpointContext";
import { MenuIcon, CollapseIcon, CloseIcon, ArrowLeft } from "./Icons";
import DefaultHeaderLogo from "./DefaultHeaderLogo";
import isMobile from "../helpers/isMobile";
import StoreContainer from "../stores/StoreContainer";

interface Config {
  config: EngagementConfiguration;
}

interface Endpoints {
  endpoints: EndpointContextProps;
}

interface Props {
  isPopout: boolean;
  onClose?(): void;
  onEmail?(): void;
  onPopout?(): void;
  onPrint?(): void;
  onCollapse(): void;
  headerContent?: React.ReactNode;
  menuFooterContent?: React.ReactNode;
}

interface State {
  isMenuOpen: boolean;
}

class Header extends React.Component<Props & Config & Endpoints, State> {
  state = {
    isMenuOpen: false
  };

  static defaultProps = {
    headerContent: <DefaultHeaderLogo />
  };

  rootNode: HTMLDivElement;

  componentDidMount() {
    // This method does not run within the window context the component is rendered
    // into, so using window.addEventListener will not work.

    if (this.rootNode && this.rootNode.ownerDocument) {
      this.rootNode.ownerDocument.addEventListener(
        "click",
        this.handleWindowClick
      );
    }

    if (window) {
      window.parent.window.addEventListener("click", this.handleWindowClick);
    }
  }

  componentWillUnmount() {
    if (this.rootNode && this.rootNode.ownerDocument) {
      this.rootNode.ownerDocument.removeEventListener(
        "click",
        this.handleWindowClick
      );
    }

    if (window) {
      window.parent.window.removeEventListener("click", this.handleWindowClick);
    }
  }

  handleWindowClick = (e: MouseEvent) => {
    e.stopPropagation();
    this.setState({ isMenuOpen: false });
  };

  handleMenuButtonClick = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    this.toggleMenu();
  };

  toggleMenu() {
    this.setState({ isMenuOpen: !this.state.isMenuOpen });
  }

  getBackgroundImage() {
    switch (this.props.config.HeaderBackgroundImage) {
      case "none":
        return null;
      case "city":
        return `url(${this.props.endpoints.images}/inline/header-bg-city.jpg)`;
      case "custom":
        return `url(${this.props.config.HeaderBackgroundUrl})`;
      default:
        return `url(${this.props.endpoints.images}/inline/header-bg/${this.props.config.HeaderBackgroundImage})`;
    }
  }

  useMinimizedHeader() {
    if (isMobile()) {
      return this.props.config.UseMinimizedHeaderMobile;
    }
    return this.props.config.UseMinimizedHeaderDesktop;
  }

  render() {
    return (
      <div
        ref={(el) => (this.rootNode = el)}
        className="velaro-header"
        style={{ backgroundImage: this.getBackgroundImage() }}
      >
        <div className="velaro-header-inner">
          <div className="velaro-header-top">
            <HeaderMenu
              isMenuOpen={this.state.isMenuOpen}
              isPopout={this.props.isPopout}
              footerContent={this.props.menuFooterContent}
              handleMenuButtonClick={(e) => this.handleMenuButtonClick(e)}
              onPopout={this.props.onPopout}
              onEmail={this.props.onEmail}
              onPrint={this.props.onPrint}
            />
            <div className="velaro-header-title velaro-text-primary">
              {this.props.config.HeaderTitle}
            </div>
            <div className="velaro-header-top-left">
              {!this.props.isPopout && isMobile() && (
                <button
                  onClick={() => this.props.onCollapse()}
                  role="button"
                  aria-label="Minimize Live Chat"
                >
                  <ArrowLeft color={this.props.config.PrimaryTextColor} />
                </button>
              )}
            </div>
            <div className="velaro-header-top-right">
              {!this.props.isPopout && !isMobile() && (
                <button
                  onClick={() => this.props.onCollapse()}
                  role="button"
                  aria-label="Minimize Live Chat"
                >
                  <CollapseIcon color={this.props.config.PrimaryTextColor} />
                </button>
              )}
              {this.props.onClose &&
                !StoreContainer.SettingStore.useIsLGCanada() && (
                  <button
                    onClick={() => this.props.onClose()}
                    role="button"
                    aria-label="Close Chat"
                  >
                    <CloseIcon color={this.props.config.PrimaryTextColor} />
                  </button>
                )}
            </div>
          </div>
          {!this.useMinimizedHeader() && this.props.headerContent && (
            <div className="velaro-header-content velaro-desktop">
              {this.props.headerContent}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default (props: Props) => (
  <ConfigContext.Consumer>
    {(config) => (
      <EndpointContext.Consumer>
        {(endpoints) => (
          <Header config={config} endpoints={endpoints} {...props} />
        )}
      </EndpointContext.Consumer>
    )}
  </ConfigContext.Consumer>
);
